import { useState } from 'react';
import { Link, useSearchParams } from 'react-router';
import {
  Users, TrendingUp, BadgeCheck, ArrowUpDown, Shield, Copy,
  Search, History, Zap,
} from 'lucide-react';
import {
  useCopySignals, useCopySubscriptions, useUnsubscribeFromCopy, useSignalDailyPnl,
  type CopySignal, type CopySubscription,
} from '@/hooks/use-copy-trade';
import { useAuth } from '@/hooks/use-auth';
import { Spinner } from '@/components/ui/spinner';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { CopyBotDrawer } from '@/components/copy-bot-drawer';
import { Modal } from '@/components/ui/modal';
import { formatPnl, pnlColor } from '@/lib/format';

type TopTab = 'copying' | 'discover';
type CopyingSubTab = 'active' | 'history';
type SortBy = 'total_pnl' | 'win_rate' | 'total_trades';

// Dicebear avatar URL from signal name (deterministic)
function dicebearAvatar(seed: string): string {
  return `https://api.dicebear.com/9.x/shapes/svg?seed=${encodeURIComponent(seed)}&backgroundColor=f97316,fb923c,fbbf24&shape1Color=f97316,ea580c`;
}

export default function CopySignalsPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const topTab: TopTab = searchParams.get('tab') === 'discover' ? 'discover' : 'copying';
  const { user } = useAuth();
  const isAdmin = user?.is_admin === true;

  const handleTabChange = (tab: TopTab) => {
    // Reset sub-state when switching top tabs to avoid stale params
    if (tab === 'discover') {
      setSearchParams({ tab: 'discover' });
    } else {
      setSearchParams({});
    }
  };

  return (
    <div className="space-y-5">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-xl font-bold text-ink flex items-center gap-2">
            <Users size={22} className="text-orange" /> Copy Trading
          </h1>
          <p className="text-xs text-ink-3 mt-1">Follow verified strategies and mirror their trades automatically</p>
        </div>
        {isAdmin && (
          <Link to="/copy-trade/admin">
            <Button variant="ghost" size="sm"><Shield size={14} className="mr-1" /> Admin</Button>
          </Link>
        )}
      </div>

      {/* Top Tabs */}
      <div className="flex items-center gap-1 border-b border-border">
        {([
          { key: 'copying' as TopTab, label: 'Copying', icon: TrendingUp },
          { key: 'discover' as TopTab, label: 'Discover', icon: Search },
        ]).map(t => (
          <button
            key={t.key}
            onClick={() => handleTabChange(t.key)}
            className={`flex items-center gap-1.5 px-4 py-2.5 text-sm font-semibold border-b-2 transition-colors cursor-pointer ${
              topTab === t.key
                ? 'border-orange text-orange'
                : 'border-transparent text-ink-3 hover:text-ink'
            }`}
          >
            <t.icon size={14} />
            {t.label}
          </button>
        ))}
      </div>

      {/* Tab Content */}
      {topTab === 'copying' && <CopyingTab />}
      {topTab === 'discover' && <DiscoverTab />}
    </div>
  );
}

// ─── COPYING TAB ────────────────────────────────────────────────

function CopyingTab() {
  const [searchParams, setSearchParams] = useSearchParams();
  const subTab: CopyingSubTab = searchParams.get('view') === 'history' ? 'history' : 'active';
  const setSubTab = (next: CopyingSubTab) => {
    const params = new URLSearchParams(searchParams);
    if (next === 'history') params.set('view', 'history');
    else params.delete('view');
    setSearchParams(params);
  };
  const { data: subscriptions, isLoading } = useCopySubscriptions();

  const active = (subscriptions ?? []).filter(s => s.status !== 'STOPPED' && !s.copierBot?.archived_at);
  const history = (subscriptions ?? []).filter(s => s.status === 'STOPPED' || !!s.copierBot?.archived_at);

  if (isLoading) return <div className="flex items-center justify-center h-48"><Spinner size="lg" /></div>;

  const hasAnySubs = (subscriptions ?? []).length > 0;

  // Empty state — no subscriptions at all
  if (!hasAnySubs) {
    return (
      <div className="glass-sm rounded-lg p-10 text-center border border-border">
        <Copy size={40} className="text-orange/40 mx-auto mb-3" />
        <h3 className="text-sm font-bold text-ink mb-1">No active copies</h3>
        <p className="text-xs text-ink-3 mb-4">
          Browse available strategies and start copying to mirror trades automatically.
        </p>
        <Link to="/copy-trade?tab=discover">
          <Button size="sm">
            <Search size={14} className="mr-1.5" /> Discover Strategies
          </Button>
        </Link>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Sub-tabs */}
      <div className="flex items-center gap-1">
        {([
          { key: 'active' as CopyingSubTab, label: 'Active', count: active.length, icon: Zap },
          { key: 'history' as CopyingSubTab, label: 'History', count: history.length, icon: History },
        ]).map(t => (
          <button
            key={t.key}
            onClick={() => setSubTab(t.key)}
            className={`flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold rounded-full transition-colors cursor-pointer ${
              subTab === t.key
                ? 'bg-orange/10 text-orange border border-orange/30'
                : 'bg-surface text-ink-3 hover:text-ink'
            }`}
          >
            <t.icon size={12} />
            {t.label}
            {t.count > 0 && (
              <span className={`ml-0.5 text-[10px] font-mono ${subTab === t.key ? 'text-orange' : 'text-ink-4'}`}>
                {t.count}
              </span>
            )}
          </button>
        ))}
      </div>

      {/* Content */}
      {subTab === 'active' && <ActiveCopies subscriptions={active} />}
      {subTab === 'history' && <CopyHistory subscriptions={history} />}
    </div>
  );
}

function ActiveCopies({ subscriptions }: { subscriptions: CopySubscription[] }) {
  const unsubscribe = useUnsubscribeFromCopy();
  const [stopSubId, setStopSubId] = useState<string | null>(null);
  const stopSub = subscriptions.find(s => s.id === stopSubId);

  if (subscriptions.length === 0) {
    return (
      <div className="glass-sm rounded-lg p-8 text-center border border-border">
        <Zap size={32} className="text-ink-4 mx-auto mb-2" />
        <p className="text-sm font-semibold text-ink-3">No active copies</p>
        <p className="text-xs text-ink-4 mt-1 mb-3">Your stopped copies are in the History tab.</p>
        <Link to="/copy-trade?tab=discover">
          <Button size="sm" variant="ghost">
            <Search size={14} className="mr-1.5" /> Discover Strategies
          </Button>
        </Link>
      </div>
    );
  }

  return (
    <>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
        {subscriptions.map(sub => {
          const pnl = sub.copierBot?.combined_pnl ?? sub.copierBot?.total_pnl ?? 0;
          const equity = sub.copierBot?.virtual_equity ?? 0;
          const avatarUrl = sub.signal?.avatar_url || dicebearAvatar(sub.signal?.name || sub.copierBot?.name || 'signal');
          return (
            <div key={sub.id} className="glass-sm rounded-lg border border-glass-border hover:border-orange/30 transition-all p-4">
              <Link to={`/bots/${sub.copierBot?.id}`} className="flex items-center gap-3">
                <img src={avatarUrl} alt="" className="w-10 h-10 rounded-full bg-surface flex-shrink-0" />
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-1.5">
                    <span className={`w-1.5 h-1.5 rounded-full flex-shrink-0 ${sub.status === 'ACTIVE' ? 'bg-green animate-pulse' : 'bg-amber'}`} />
                    <span className="text-sm font-bold text-ink truncate">{sub.signal?.name || sub.copierBot?.name}</span>
                  </div>
                  <div className="flex items-center gap-3 mt-1">
                    <span className="text-[10px] text-ink-4">Equity <span className="font-mono font-semibold text-ink">${equity.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span></span>
                    <span className={`text-[10px] font-mono font-semibold ${pnlColor(pnl)}`}>{formatPnl(pnl)}</span>
                  </div>
                </div>
              </Link>
              <div className="flex items-center justify-end gap-2 mt-3 pt-3 border-t border-border">
                <Link to={`/bots/${sub.copierBot?.id}`}>
                  <Button variant="ghost" size="sm" className="text-[10px]">View Bot</Button>
                </Link>
                <Button variant="danger" size="sm" className="text-[10px]" onClick={() => setStopSubId(sub.id)}>
                  Stop Copying
                </Button>
              </div>
            </div>
          );
        })}
      </div>

      <Modal
        open={!!stopSubId}
        onClose={() => !unsubscribe.isPending && setStopSubId(null)}
        title="Stop Copying"
        footer={
          <>
            <Button variant="ghost" size="sm" onClick={() => setStopSubId(null)} disabled={unsubscribe.isPending}>Cancel</Button>
            <Button variant="danger" size="sm" disabled={unsubscribe.isPending}
              onClick={() => {
                if (stopSubId) {
                  unsubscribe.mutate(stopSubId, { onSuccess: () => setStopSubId(null) });
                }
              }}>
              {unsubscribe.isPending ? 'Stopping...' : 'Stop Copying'}
            </Button>
          </>
        }
      >
        <div className="space-y-2">
          <p className="text-sm font-semibold">Stop copying {stopSub?.signal?.name || stopSub?.copierBot?.name}?</p>
          <p className="text-xs text-ink-3 leading-relaxed">
            This will close all open positions at market price and archive the copy bot. This action cannot be undone.
          </p>
        </div>
      </Modal>
    </>
  );
}

function CopyHistory({ subscriptions }: { subscriptions: CopySubscription[] }) {
  if (subscriptions.length === 0) {
    return (
      <div className="glass-sm rounded-lg p-8 text-center border border-border">
        <History size={32} className="text-ink-4 mx-auto mb-2" />
        <p className="text-sm font-semibold text-ink-3">No copy history</p>
        <p className="text-xs text-ink-4 mt-1">Stopped copies will appear here.</p>
      </div>
    );
  }

  return (
    <div className="space-y-1.5">
      {subscriptions.map(sub => {
        const pnl = sub.copierBot?.total_pnl || sub.total_profit || 0;
        const displayName = sub.signal?.name || sub.copierBot?.name?.replace(/^Copy:\s*/, '') || 'Deleted Signal';
        const avatarUrl = sub.signal?.avatar_url || dicebearAvatar(displayName);
        const startDate = new Date(sub.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
        // Navigate to bot detail if available, signal detail as fallback
        const linkTo = sub.copierBot?.id
          ? `/bots/${sub.copierBot.id}`
          : sub.signal?.id
            ? `/copy-trade/${sub.signal.id}`
            : null;

        const card = (
          <div className={`glass-sm rounded-lg border border-glass-border p-3 flex items-center gap-3 transition-all ${linkTo ? 'hover:border-orange/30 cursor-pointer' : ''}`}>
            <img src={avatarUrl} alt="" className="w-9 h-9 rounded-full bg-surface flex-shrink-0 opacity-60" />
            <div className="flex-1 min-w-0">
              <span className="text-xs font-bold text-ink-3 truncate block">{displayName}</span>
              <span className="text-[10px] text-ink-4">Started {startDate}</span>
            </div>
            <div className="flex items-center gap-2 flex-shrink-0">
              <span className={`text-xs font-mono font-bold ${pnlColor(pnl)}`}>{formatPnl(pnl)}</span>
              <Badge color="default">Stopped</Badge>
            </div>
          </div>
        );

        return linkTo ? (
          <Link key={sub.id} to={linkTo} className="block">{card}</Link>
        ) : (
          <div key={sub.id}>{card}</div>
        );
      })}
    </div>
  );
}

// ─── DISCOVER TAB ───────────────────────────────────────────────

function DiscoverTab() {
  const [searchParams, setSearchParams] = useSearchParams();
  const sortParam = searchParams.get('sort');
  const sortBy: SortBy = (sortParam === 'win_rate' || sortParam === 'total_trades') ? sortParam : 'total_pnl';
  const setSortBy = (next: SortBy) => {
    const params = new URLSearchParams(searchParams);
    if (next === 'total_pnl') params.delete('sort');
    else params.set('sort', next);
    setSearchParams(params);
  };
  const { data, isLoading } = useCopySignals({ sortBy });
  const { data: subscriptions } = useCopySubscriptions();

  const signals = data?.signals ?? [];
  const subscribedSignalIds = new Set(
    (subscriptions ?? []).filter(s => s.status !== 'STOPPED' && !s.copierBot?.archived_at).map(s => s.signal?.id),
  );

  if (isLoading) return <div className="flex items-center justify-center h-48"><Spinner size="lg" /></div>;

  return (
    <div className="space-y-4">
      {/* Sort controls */}
      <div className="flex items-center gap-2">
        <div className="flex items-center gap-1 text-xs text-ink-3"><ArrowUpDown size={12} /> Sort:</div>
        {([
          { key: 'total_pnl', label: 'P&L' },
          { key: 'win_rate', label: 'Win Rate' },
          { key: 'total_trades', label: 'Trades' },
        ] as const).map(s => (
          <button key={s.key} onClick={() => setSortBy(s.key)}
            className={`px-2.5 py-1 text-xs font-medium rounded-full transition-colors cursor-pointer ${
              sortBy === s.key ? 'bg-orange/10 text-orange border border-orange/30' : 'bg-surface text-ink-3 hover:text-ink'
            }`}>{s.label}</button>
        ))}
      </div>

      {signals.length === 0 ? (
        <div className="text-center py-16">
          <Users size={40} className="text-ink-4 mx-auto mb-3" />
          <p className="text-sm text-ink-3 font-semibold">No strategies available yet</p>
          <p className="text-xs text-ink-4 mt-1">Strategies will appear here once published</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
          {signals.map(signal => (
            <SignalCard key={signal.id} signal={signal} isSubscribed={subscribedSignalIds.has(signal.id)} />
          ))}
        </div>
      )}
    </div>
  );
}

// ─── SIGNAL CARD ────────────────────────────────────────────────

function SignalCard({ signal, isSubscribed }: { signal: CopySignal & { copier_count?: number }; isSubscribed: boolean }) {
  const [showDrawer, setShowDrawer] = useState(false);
  const { data: dailyPnl } = useSignalDailyPnl(signal.id, 30);
  const symbolDisplay = signal.symbol_filter?.join(', ') || 'All Symbols';
  const avatarUrl = signal.avatar_url || dicebearAvatar(signal.name);
  const copierCount = (signal as any).copier_count ?? 0;

  return (
    <div className="glass-sm rounded-lg border border-glass-border hover:border-orange/30 transition-all overflow-hidden">
      {/* Sparkline area */}
      <Link to={`/copy-trade/${signal.id}`} className="block p-4 pb-2">
        <div className="flex items-start gap-3 mb-3">
          <img src={avatarUrl} alt={signal.name} className="w-10 h-10 rounded-full object-cover flex-shrink-0 bg-surface" />
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-1.5">
              <h3 className="text-sm font-bold text-ink truncate">{signal.name}</h3>
              {signal.is_verified && <BadgeCheck size={14} className="text-blue-500 flex-shrink-0" />}
            </div>
            <span className="text-[10px] text-ink-4">{symbolDisplay}</span>
          </div>
          {isSubscribed && <Badge color="green">Copying</Badge>}
        </div>

        {/* 30D Sparkline */}
        <div className="h-12 mb-2">
          {dailyPnl && dailyPnl.length > 1 ? (
            <Sparkline data={dailyPnl.map(d => d.cumulative)} color={signal.total_pnl >= 0 ? '#059669' : '#dc2626'} />
          ) : (
            <div className="h-full flex items-center justify-center">
              <span className="text-[9px] text-ink-4">Collecting data...</span>
            </div>
          )}
        </div>

        {/* Stats */}
        <div className="grid grid-cols-4 gap-2 pt-2 border-t border-border">
          <div>
            <p className="text-[9px] text-ink-4">P&L</p>
            <p className={`text-xs font-bold font-mono ${pnlColor(signal.total_pnl)}`}>{formatPnl(signal.total_pnl)}</p>
          </div>
          <div>
            <p className="text-[9px] text-ink-4">Win Rate</p>
            <p className="text-xs font-bold text-ink font-mono">{signal.total_trades > 0 ? `${signal.win_rate.toFixed(0)}%` : '—'}</p>
          </div>
          <div>
            <p className="text-[9px] text-ink-4">Trades</p>
            <p className="text-xs font-bold text-ink font-mono">{signal.total_trades}</p>
          </div>
          <div>
            <p className="text-[9px] text-ink-4">Copiers</p>
            <p className="text-xs font-bold text-ink font-mono">{copierCount}</p>
          </div>
        </div>
      </Link>

      {/* Copy button */}
      {!isSubscribed && (
        <div className="px-4 pb-3">
          <button
            onClick={() => setShowDrawer(true)}
            className="w-full py-2 text-xs font-bold rounded-md bg-gradient-to-br from-orange to-amber text-white hover:brightness-110 transition-all cursor-pointer flex items-center justify-center gap-1.5"
          >
            <Copy size={12} /> Copy
          </button>
        </div>
      )}

      <CopyBotDrawer open={showDrawer} signal={signal} onClose={() => setShowDrawer(false)} />
    </div>
  );
}

/** Simple SVG sparkline chart */
function Sparkline({ data, color }: { data: number[]; color: string }) {
  if (data.length < 2) return null;

  const min = Math.min(...data);
  const max = Math.max(...data);
  const range = max - min || 1;
  const w = 200;
  const h = 48;
  const pad = 2;

  const points = data.map((v, i) => {
    const x = pad + (i / (data.length - 1)) * (w - pad * 2);
    const y = h - pad - ((v - min) / range) * (h - pad * 2);
    return `${x},${y}`;
  }).join(' ');

  // Area fill
  const firstX = pad;
  const lastX = pad + ((data.length - 1) / (data.length - 1)) * (w - pad * 2);
  const areaPoints = `${firstX},${h} ${points} ${lastX},${h}`;

  return (
    <svg viewBox={`0 0 ${w} ${h}`} className="w-full h-full" preserveAspectRatio="none">
      <polygon points={areaPoints} fill={color} opacity="0.1" />
      <polyline points={points} fill="none" stroke={color} strokeWidth="1.5" strokeLinejoin="round" />
    </svg>
  );
}
